import Cookies from 'universal-cookie';
import * as Const from '../Const';
import axios, { AxiosResponse } from 'axios';
import {
  UploadProgressHandler,
  UploadProtocolError,
  UploadPurpose,
  UploadResult,
  UploadSessionRequest,
  UploadSessionResponse,
  getUploadFileExtension,
  isAllowedUploadExtension,
} from './FileUpload.logic';
import { ResumableUploadClient } from './ResumableUpload';

const UPLOAD_SESSION_TIMEOUT_MILLISECONDS = 30000;
const STORAGE_SESSION_URI_PREFIX = 'https://storage.googleapis.com/';
const OBJECT_NAME_PATTERN = /^files\/[a-z0-9][a-z0-9-]*\/(face-drawing|face-image|frame-drawing|frame-image)\/[0-9a-f]{32}\.(dxf|pdf|png|jpg|jpeg)$/;
const SESSION_RESPONSE_KEYS = ['contentType', 'objectName', 'sessionUri'];

const EXTENSION_CONTENT_TYPES: { [extension: string]: string } = {
  dxf: 'text/plain',
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
};

// 例外の本文に応答の中身や送信先のアドレスを含めない
function readUploadSessionResponse(value: unknown, purpose: UploadPurpose, fileName: string): UploadSessionResponse {
  if (isSessionResponseShape(value)) {
    const validObjectName = matchesRequestedFile(value.objectName, purpose, fileName);
    const validContentType = value.contentType === EXTENSION_CONTENT_TYPES[getUploadFileExtension(fileName) ?? ''];
    if (validObjectName && validContentType && isStorageSessionUri(value.sessionUri)) {
      return {
        objectName: value.objectName,
        sessionUri: value.sessionUri,
        contentType: value.contentType,
      };
    }
  }
  throw new UploadProtocolError();
}

function isSessionResponseShape(value: unknown): value is UploadSessionResponse {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const record = value as { [key: string]: unknown };
    const keys = Object.keys(record).sort();
    const hasOnlyExpectedKeys = keys.length === SESSION_RESPONSE_KEYS.length && keys.every((key, index) => key === SESSION_RESPONSE_KEYS[index]);
    return SESSION_RESPONSE_KEYS.every((key) => typeof record[key] === 'string') && hasOnlyExpectedKeys;
  }
  return false;
}

function matchesRequestedFile(objectName: string, purpose: UploadPurpose, fileName: string): boolean {
  const match = OBJECT_NAME_PATTERN.exec(objectName);
  if (match !== null && isAllowedUploadExtension(purpose, fileName)) {
    return match[1] === purpose && match[2] === getUploadFileExtension(fileName);
  }
  return false;
}

// 利用者名や別のポートが紛れ込んだ送信先は断る
function isStorageSessionUri(sessionUri: string): boolean {
  if (sessionUri.startsWith(STORAGE_SESSION_URI_PREFIX)) {
    const parsed = new URL(sessionUri);
    const hasOnlyExpectedParts = parsed.username === '' && parsed.password === '' && parsed.port === '' && parsed.hash === '';
    return parsed.protocol === 'https:' && parsed.hostname === 'storage.googleapis.com' && hasOnlyExpectedParts;
  }
  return false;
}

type Fetch = {
  (input: RequestInfo, init?: RequestInit): Promise<Response>;
};

const ApiHeaders = {
  'content-type': 'application/json',
};

class HttpClient {
  private readonly baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  post<T>(
    entryPoint: string,
    body: T
  ) {
    return axios.post(this.baseUrl + entryPoint, body, {
      headers: this.buildHeaders(),
    });
  }

  // ファイルを Cloud Storage へ直接送るための送信先をサーバーに発行してもらう
  postUploadSession(body: UploadSessionRequest): Promise<AxiosResponse> {
    return axios.post(this.baseUrl + '/files/upload-sessions', body, {
      headers: this.buildHeaders(),
      timeout: UPLOAD_SESSION_TIMEOUT_MILLISECONDS,
      withCredentials: false,
    });
  }

  private buildHeaders(): { [key: string]: string } {
    const headers: { [key: string]: string } = { ...ApiHeaders };

    const cookies = new Cookies();
    const authToken = cookies.get<string>('auth-token');
    if (authToken) {
      headers['x-account-auth-token'] = authToken;
    }

    return headers;
  }
}

export class ApiClient {
  private readonly http: HttpClient;
  private readonly directUploads: ResumableUploadClient;

  constructor() {
    this.http = new HttpClient(Const.API_URL);
    this.directUploads = new ResumableUploadClient(this.issueUploadSession.bind(this));
  }

  async login(data: { screen_id: string; password: string }): Promise<void> {
    const response = await this.http.post(
      '/v1/auth/login',
      JSON.stringify(data)
    );
    const loginData = response.data.result;
    const expiredAt = new Date(loginData.expired);

    const cookies = new Cookies();
    cookies.set('auth-token', loginData.token, {
      expires: expiredAt,
      path: '/',
    });
  }

  async getAppinfo(): Promise<{ companyName: string }> {
    const response = await axios.get(Const.API_URL + '/appInfo');
    const appInfo = response.data;
    return {
      companyName: appInfo.company_name
    };
  }

  private async issueUploadSession(
    purpose: UploadPurpose,
    fileName: string,
    fileSize: number
  ): Promise<UploadSessionResponse> {
    const response = await this.http.postUploadSession({ purpose, fileName, fileSize });
    return readUploadSessionResponse(response.data, purpose, fileName);
  }

  // ブラウザから Cloud Storage へ直接送る。同じ ApiClient の中では送信済みの結果を再利用する
  uploadDirect(file: File, purpose: UploadPurpose, onProgress?: UploadProgressHandler): Promise<UploadResult> {
    return this.directUploads.upload(file, purpose, onProgress);
  }

  disposeDirectUploads(): void {
    this.directUploads.dispose();
  }
}
